import { zodResolver } from '@hookform/resolvers/zod'
import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { getRepayOptions } from './assets'
import { getFormFieldsForRepayDialog, getRepayDialogFormValidator, getUserDebtToRepay } from './form'
import { makeUpdatedPositionOverview } from './positionOverview'
import { PositionOverview } from './types'
import { useCreateRepayObjectives } from './useCreateRepayObjectives'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { TokenWithBalance } from '@/domain/common/types'
import { useConditionalFreeze } from '@/domain/hooks/useConditionalFreeze'
import { useSandbox } from '@/domain/sandbox'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import {
  AssetInputSchema,
  DialogFormNormalizedData,
  useDebouncedDialogFormValues,
} from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useAaveDataLayer } from '@/features/sparklend/common/data/market-info/aave-data-layer/useAaveDataLayer'
import { EPOCH_LENGTH } from '@/features/sparklend/common/data/market-info/consts'
import { Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { updatePositionSummary } from '@/features/sparklend/common/data/market-info/updatePositionSummary'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { useMarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { getRepayMaxValue } from '@/features/sparklend/common/utils/action-max-value-getters/getRepayMaxValue'
import { SparkLendToken } from '@/features/sparklend/tokens/SparkLendToken'

export interface UseRepayDialogOptions {
  reserve: Reserve
  initialRepayToken: SparkLendToken
}

interface SparkLendTokenWithValue {
  token: SparkLendToken
  value: NormalizedNumber
}

export interface UseRepayDialogResult {
  dialogTitle: string
  repayOptions: TokenWithBalance[]
  assetsToRepayFields: FormFieldsForDialog
  repayAsset: SparkLendTokenWithValue
  inputAsset: SparkLendTokenWithValue
  actionsViewState: ActionsViewState
  pageStatus: PageStatus
  form: UseFormReturn<AssetInputSchema>
  currentPositionOverview: PositionOverview
  updatedPositionOverview?: PositionOverview
}

export function useRepayDialog({ reserve, initialRepayToken }: UseRepayDialogOptions): UseRepayDialogResult {
  const chainId = useChainId()
  const { aaveData } = useAaveDataLayer({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })
  const { marketInfo: marketInfoIn1Epoch } = useMarketInfo({ timeAdvance: EPOCH_LENGTH, chainId })
  const { marketInfo: marketInfoIn2Epochs } = useMarketInfo({ timeAdvance: 2 * EPOCH_LENGTH, chainId })

  const walletInfo = useMarketWalletInfo({ chainId })

  const nativeAssetInfo = getNativeAssetInfo(marketInfo.chainId)
  const { isInSandbox } = useSandbox()
  const reservePosition = marketInfo.findOnePositionByToken(reserve.token)

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(getRepayDialogFormValidator(marketInfo, walletInfo, reserve)),
    defaultValues: {
      symbol: initialRepayToken.symbol,
      value: '',
      isMaxSelected: false,
    },
    mode: 'onChange',
  })

  const repayOptions = getRepayOptions({
    reserve,
    marketInfo,
    walletInfo,
    nativeAssetInfo,
    isInSandbox,
  })

  const {
    debouncedFormValues: _formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedDialogFormValues({
    form,
    marketInfo,
  })
  const formValues: DialogFormNormalizedData = useConditionalFreeze(_formValues, pageStatus === 'success')
  const payToken = formValues.token

  const payerDebt = getUserDebtToRepay({ marketInfo, reserve, payToken })
  const repayMaxValue = getRepayMaxValue({
    asset: {
      status: reserve.status,
      isNativeAsset: payToken.symbol === marketInfo.nativeAssetInfo.nativeAssetSymbol,
    },
    user: {
      balance: walletInfo.findWalletBalanceForSymbol(payToken.symbol),
      debt: payerDebt,
    },
    chain: {
      minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
    },
  })

  const assetsToRepayFields = getFormFieldsForRepayDialog({
    form,
    marketInfo,
    walletInfo,
    repayMaxValue,
  })

  const leftoverDebt = NormalizedNumber.max(
    reservePosition.borrowBalance.minus(formValues.value),
    NormalizedNumber.ZERO,
  )
  const objectives = useCreateRepayObjectives({
    formData: formValues,
    reserve,
    marketInfoIn1Epoch,
    marketInfoIn2Epochs,
    walletInfo,
  })

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      chainId,
      marketInfo,
    },
    onFinish: () => setPageStatus('success'),
    enabled: formValues.value.gt(0) && isFormValid && !isDebouncing,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [formValues.token.symbol, formValues.value.toString(), formValues.isMaxSelected, resetActions])

  const currentPositionOverview = {
    healthFactor: marketInfo.userPositionSummary.healthFactor,
    debt: reservePosition.borrowBalance,
  }

  const updatedUserSummary = updatePositionSummary({
    repays: [
      {
        reserve,
        value: formValues.value,
      },
    ],
    marketInfo,
    aaveData,
    nativeAssetInfo,
  })

  const updatedPositionOverview = formValues.value.eq(0)
    ? undefined
    : makeUpdatedPositionOverview({
        healthFactor: updatedUserSummary.healthFactor,
        leftoverDebt,
      })

  const dialogTitle =
    reserve.token.symbol === formValues.token.symbol ||
    formValues.token.isAToken ||
    formValues.token.symbol === nativeAssetInfo.nativeAssetSymbol
      ? `Repay ${reserve.token.symbol}`
      : `Swap to ${reserve.token.symbol} and repay`

  const repayAsset =
    formValues.token.symbol === nativeAssetInfo.nativeAssetSymbol
      ? (() => {
          const nativeAsset = marketInfo.findOnePositionBySymbol(nativeAssetInfo.nativeAssetSymbol).reserve.token
          return {
            token: nativeAsset,
            value: formValues.value,
          }
        })()
      : {
          token: reserve.token,
          value: formValues.value,
        }
  const inputAsset = {
    token: formValues.token,
    value: formValues.value,
  }

  return {
    dialogTitle,
    form,
    repayOptions,
    assetsToRepayFields,
    repayAsset,
    inputAsset,
    actionsViewState,
    pageStatus: {
      state: pageStatus,
      actionsEnabled: formValues.value.gt(0) && isFormValid && !isDebouncing,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    currentPositionOverview,
    updatedPositionOverview,
  }
}
